import _ from "lodash";
import { CustomClaimMessages, UserRightsMessages } from "src/languages/interfaces";
import { RoleClaimType, RoleType, UserClaimType, UserType } from "src/store/graphql/helpers/types.graphql";

export interface IRoleWithUsers extends RoleType {
  users: UserType[];
}

export const ERoutes = {
  home: "/",
  confirmAccount: "confirm-account",
  maintenanceUser: "maintenanceuser",
  identity: "identity",
  selectTenant: "select-tenant",
  // LEGACY MIGRATION (temporary)
  legacyMigration: "legacy-migration",
  users: "users",
  notifications: "notifications",
  login: "/login",
  launch: "/launch"
};

export const userClaimTypes = {
  defaultClaim: {
    id: "defaultClaim",
    color: "#003563",
    caption: CustomClaimMessages.defaultClaims
  },
  additionalUserClaim: {
    id: "additionalUserClaim",
    color: "#99CC33",
    caption: CustomClaimMessages.additionalUserClaim
  },
  restrictedUserClaim: {
    id: "restrictedUserClaim",
    color: "#A3A3A3",
    caption: CustomClaimMessages.restrictedUserClaim
  }
};

export enum EColors {
  DEFAULT = "default",
  PRIMARY = "primary",
  SECONDARY = "secondary",
  SUCCESS = "success",
  WARNING = "warning",
  ERROR = "error",
  INFO = "info"
}

export const sortAlphabetically = <T extends object>(array: T[], property: keyof T): T[] => {
  const arrayClone = _.cloneDeep(array);

  return arrayClone.sort((a, b) => String(a[property]).localeCompare(String(b[property])));
};

export const sortByBoolean = <T extends object>(array: T[], property: keyof T): T[] => {
  const arrayClone = _.cloneDeep(array);

  return arrayClone.sort((a, b) => Number(b[property]) - Number(a[property]));
};

export const getAllClaimsAttachedToUser = (userClaims: UserClaimType[], roleClaims?: RoleClaimType[]) => {
  if (!roleClaims) return [];

  const revokedClaims: { claimId: string }[] = [];
  const additionalClaims: { claimId: string }[] = [];

  userClaims.forEach((userClaim) => {
    if (userClaim.revoked) {
      revokedClaims.push(userClaim);
    } else {
      additionalClaims.push(userClaim);
    }
  });

  const filteredRoleClaims = roleClaims?.filter(
    (roleClaim) => !revokedClaims.some((revokedClaim) => revokedClaim.claimId === roleClaim.claimId)
  );

  return [...filteredRoleClaims, ...additionalClaims];
};

export const groupUsersByRoleId = (
  users: UserType[],
  roles: RoleType[],
  selectedModuleId: string,
  groupUsersWithNoRole = true
) => {
  const usersWithRoleOnSelectedProduct = users.map((user) => {
    const selectedModuleUserRole = user
      .userRoles?.find(
        (userRole) => userRole?.role?.moduleId === selectedModuleId
      );

    return { ...user, selectedModuleUserRole };
  });

  const groupedUsers = _.groupBy(usersWithRoleOnSelectedProduct, "selectedModuleUserRole.roleId");

  const rolesWithUsers = roles.map((role) => {
    const users = groupedUsers[role.id] || [];

    return { ...role, users };
  });

  const sortedRoles = sortAlphabetically(rolesWithUsers, "name");

  if (!groupUsersWithNoRole || _.isEmpty(groupedUsers["undefined"])) return sortedRoles as IRoleWithUsers[];

  const ungroupedUsers = groupedUsers["undefined"] || [];

  sortedRoles.push({
    id: "ungrouped",
    name: UserRightsMessages.usersWithoutRole,
    alias: UserRightsMessages.usersWithoutRole,
    order: 0,
    users: ungroupedUsers,
    active: true,
    createdAt: undefined,
    deleted: false,
    moduleId: "",
    tenantId: ""
  });

  return sortedRoles as IRoleWithUsers[];
};
